<!-- src/views/SessionLogs.vue -->

<template>
  <div class="min-h-screen bg-layer-1 text-text-neutral-high p-4 md:p-8">
    <div class="mx-auto flex max-w-[1400px] flex-col gap-6">
      <header class="flex flex-col gap-4 rounded-[24px] bg-layer-2 p-5 dropshadow-heavy md:flex-row md:items-start md:justify-between">
        <div>
          <div class="flex items-center gap-3">
            <DocumentTextIcon class="h-8 w-8 text-primary" />

            <h1 class="text-[28px] font-semibold leading-tight text-text-neutral-high">
              Session Logs
            </h1>
          </div>

          <p class="mt-2 text-sm text-text-neutral-medium">
            Socket, connection and client error logs stored in this browser.
          </p>
        </div>

        <div class="flex flex-wrap gap-3">
          <button
              type="button"
              class="inline-flex cursor-pointer items-center gap-2 rounded-[12px] bg-button-secondary-mono-resting px-4 py-2 text-sm font-semibold text-text-neutral-high transition hover:opacity-85"
              @click="refreshLogs"
          >
            <ArrowPathIcon class="h-5 w-5" />
            Refresh
          </button>

          <button
              type="button"
              class="inline-flex cursor-pointer items-center gap-2 rounded-[12px] bg-red-500/20 px-4 py-2 text-sm font-semibold text-red-300 transition hover:bg-red-500/30 disabled:cursor-not-allowed disabled:opacity-40"
              :disabled="sessions.length === 0"
              @click="clearAllLogs"
          >
            <TrashIcon class="h-5 w-5" />
            Clear all
          </button>

          <label
              class="inline-flex cursor-pointer items-center gap-2 rounded-[12px] bg-button-secondary-mono-resting px-4 py-2 text-sm font-semibold text-text-neutral-high transition hover:opacity-85"
          >
            <ArrowUpTrayIcon class="h-5 w-5" />
            Import JSON

            <input
                ref="importInput"
                type="file"
                accept="application/json,.json"
                class="hidden"
                @change="importLogsFromFile"
            />
          </label>
        </div>
      </header>

      <div
          v-if="sessions.length === 0"
          class="flex min-h-[280px] flex-col items-center justify-center rounded-[24px] bg-layer-2 p-8 text-center dropshadow-heavy"
      >
        <InboxIcon class="mb-4 h-12 w-12 text-text-neutral-low" />

        <h2 class="text-xl font-semibold text-text-neutral-high">
          No session logs found
        </h2>

        <p class="mt-2 max-w-md text-sm text-text-neutral-medium">
          Open a session and generate socket events. Logs will appear here after they are saved in this browser.
        </p>
      </div>

      <div v-else class="grid grid-cols-1 gap-6 lg:grid-cols-[340px_1fr]">
        <aside class="rounded-[24px] bg-layer-2 p-4 dropshadow-heavy">
          <div class="mb-4 flex items-center justify-between">
            <h2 class="text-base font-semibold text-text-neutral-high">
              Sessions
            </h2>

            <span class="rounded-full bg-elements-neutral-3 px-3 py-1 text-xs font-semibold text-text-neutral-medium">
              {{ sessions.length }}
            </span>
          </div>

          <div class="flex max-h-[calc(100vh-220px)] flex-col gap-2 overflow-auto pr-1">
            <button
                v-for="session in sessions"
                :key="session.roomCode"
                type="button"
                class="group cursor-pointer rounded-[16px] border p-4 text-left transition"
                :class="selectedRoomCode === session.roomCode
                  ? 'border-primary bg-primary/10'
                  : 'border-white/10 bg-layer-3 hover:border-primary/50'"
                @click="selectSession(session.roomCode)"
            >
              <div class="flex items-start justify-between gap-3">
                <div class="min-w-0">
                  <div class="truncate text-lg font-semibold text-text-neutral-high">
                    {{ session.roomCode }}
                  </div>

                  <div class="mt-1 text-xs text-text-neutral-medium">
                    {{ session.entryCount }} events
                  </div>
                </div>

                <ChevronRightIcon
                    class="h-5 w-5 shrink-0 text-text-neutral-low transition group-hover:text-primary"
                    :class="{ 'text-primary': selectedRoomCode === session.roomCode }"
                />
              </div>

              <div class="mt-3 flex items-center gap-2 text-xs text-text-neutral-low">
                <ClockIcon class="h-4 w-4" />
                <span>{{ formatDate(session.updatedAt) }}</span>
              </div>
            </button>
          </div>
        </aside>

        <main v-if="selectedSession" class="min-w-0">
          <section class="mb-6 rounded-[24px] bg-layer-2 p-5 dropshadow-heavy">
            <div class="flex flex-col gap-4 xl:flex-row xl:items-start xl:justify-between">
              <div class="min-w-0">
                <div class="flex items-center gap-3">
                  <ServerStackIcon class="h-7 w-7 text-primary" />

                  <h2 class="truncate text-2xl font-semibold text-text-neutral-high">
                    Room {{ selectedSession.roomCode }}
                  </h2>
                </div>

                <div class="mt-4 grid grid-cols-1 gap-3 text-sm text-text-neutral-medium md:grid-cols-3">
                  <div class="rounded-[14px] bg-layer-3 p-3">
                    <div class="text-xs text-text-neutral-low">
                      Started
                    </div>

                    <div class="mt-1 font-medium text-text-neutral-high">
                      {{ formatDate(selectedSession.startedAt) }}
                    </div>
                  </div>

                  <div class="rounded-[14px] bg-layer-3 p-3">
                    <div class="text-xs text-text-neutral-low">
                      Updated
                    </div>

                    <div class="mt-1 font-medium text-text-neutral-high">
                      {{ formatDate(selectedSession.updatedAt) }}
                    </div>
                  </div>

                  <div class="rounded-[14px] bg-layer-3 p-3">
                    <div class="text-xs text-text-neutral-low">
                      User
                    </div>

                    <div class="mt-1 truncate font-medium text-text-neutral-high">
                      {{ selectedSession.userId || '—' }}
                    </div>
                  </div>
                </div>
              </div>

              <div class="flex flex-wrap gap-3">
                <button
                    type="button"
                    class="inline-flex cursor-pointer items-center gap-2 rounded-[12px] bg-button-secondary-mono-resting px-4 py-2 text-sm font-semibold text-text-neutral-high transition hover:opacity-85"
                    @click="exportSelectedSession"
                >
                  <ArrowDownTrayIcon class="h-5 w-5" />
                  Export JSON
                </button>

                <button
                    type="button"
                    class="inline-flex cursor-pointer items-center gap-2 rounded-[12px] bg-red-500/20 px-4 py-2 text-sm font-semibold text-red-300 transition hover:bg-red-500/30"
                    @click="clearSelectedSession"
                >
                  <TrashIcon class="h-5 w-5" />
                  Clear events
                </button>
              </div>
            </div>
          </section>

          <section class="mb-4 rounded-[24px] bg-layer-2 p-4 dropshadow-heavy">
            <div class="flex flex-col gap-3 xl:flex-row xl:items-center">
              <div class="relative flex-1">
                <MagnifyingGlassIcon class="pointer-events-none absolute left-4 top-1/2 h-5 w-5 -translate-y-1/2 text-text-neutral-low" />

                <input
                    v-model="search"
                    type="text"
                    class="h-12 w-full rounded-[14px] border border-white/10 bg-layer-3 py-2 pl-11 pr-4 text-sm text-text-neutral-high outline-none transition placeholder:text-text-neutral-low focus:border-primary"
                    placeholder="Search by event, type, payload..."
                />
              </div>

              <Listbox v-model="selectedType">
                <div class="relative min-w-[220px]">
                  <ListboxButton class="flex h-12 w-full cursor-pointer items-center justify-between rounded-[14px] border border-white/10 bg-layer-3 px-4 text-left text-sm text-text-neutral-high outline-none transition hover:border-primary/50">
                    <span>{{ selectedType || 'All types' }}</span>
                    <ChevronUpDownIcon class="h-5 w-5 text-text-neutral-low" />
                  </ListboxButton>

                  <Transition
                      leave-active-class="transition duration-100 ease-in"
                      leave-from-class="opacity-100"
                      leave-to-class="opacity-0"
                  >
                    <ListboxOptions class="absolute right-0 z-20 mt-2 max-h-72 w-full overflow-auto rounded-[14px] border border-white/10 bg-layer-3 p-1 shadow-xl outline-none">
                      <ListboxOption v-slot="{ active, selected }" value="">
                        <div
                            class="flex cursor-pointer items-center justify-between rounded-[10px] px-3 py-2 text-sm"
                            :class="active ? 'bg-primary/15 text-text-neutral-high' : 'text-text-neutral-medium'"
                        >
                          <span>All types</span>
                          <CheckIcon v-if="selected" class="h-4 w-4 text-primary" />
                        </div>
                      </ListboxOption>

                      <ListboxOption
                          v-for="type in availableTypes"
                          :key="type"
                          v-slot="{ active, selected }"
                          :value="type"
                      >
                        <div
                            class="flex cursor-pointer items-center justify-between rounded-[10px] px-3 py-2 text-sm"
                            :class="active ? 'bg-primary/15 text-text-neutral-high' : 'text-text-neutral-medium'"
                        >
                          <span>{{ type }}</span>
                          <CheckIcon v-if="selected" class="h-4 w-4 text-primary" />
                        </div>
                      </ListboxOption>
                    </ListboxOptions>
                  </Transition>
                </div>
              </Listbox>

              <Listbox v-if="availableSenders.length" v-model="selectedSender">
                <div class="relative min-w-[220px]">
                  <ListboxButton class="flex h-12 w-full cursor-pointer items-center justify-between rounded-[14px] border border-white/10 bg-layer-3 px-4 text-left text-sm text-text-neutral-high outline-none transition hover:border-primary/50">
                    <span class="truncate">{{ selectedSender || 'All senders' }}</span>
                    <ChevronUpDownIcon class="h-5 w-5 shrink-0 text-text-neutral-low" />
                  </ListboxButton>

                  <Transition
                      leave-active-class="transition duration-100 ease-in"
                      leave-from-class="opacity-100"
                      leave-to-class="opacity-0"
                  >
                    <ListboxOptions class="absolute right-0 z-20 mt-2 max-h-72 w-full overflow-auto rounded-[14px] border border-white/10 bg-layer-3 p-1 shadow-xl outline-none">
                      <ListboxOption v-slot="{ active, selected }" value="">
                        <div
                            class="flex cursor-pointer items-center justify-between rounded-[10px] px-3 py-2 text-sm"
                            :class="active ? 'bg-primary/15 text-text-neutral-high' : 'text-text-neutral-medium'"
                        >
                          <span>All senders</span>
                          <CheckIcon v-if="selected" class="h-4 w-4 text-primary" />
                        </div>
                      </ListboxOption>

                      <ListboxOption
                          v-for="sender in availableSenders"
                          :key="sender"
                          v-slot="{ active, selected }"
                          :value="sender"
                      >
                        <div
                            class="flex cursor-pointer items-center justify-between gap-2 rounded-[10px] px-3 py-2 text-sm"
                            :class="active ? 'bg-primary/15 text-text-neutral-high' : 'text-text-neutral-medium'"
                        >
                          <span class="truncate">{{ sender }}</span>
                          <CheckIcon v-if="selected" class="h-4 w-4 shrink-0 text-primary" />
                        </div>
                      </ListboxOption>
                    </ListboxOptions>
                  </Transition>
                </div>
              </Listbox>

              <button
                  type="button"
                  class="inline-flex h-12 cursor-pointer items-center justify-center gap-2 rounded-[14px] border border-white/10 bg-layer-3 px-4 text-sm font-semibold text-text-neutral-high outline-none transition hover:border-primary/50 hover:opacity-85"
                  @click="toggleSortDirection"
              >
                <ArrowsUpDownIcon class="h-5 w-5 text-text-neutral-low" />
                {{ sortDirectionLabel }}
              </button>

              <div class="rounded-[14px] bg-layer-3 px-4 py-3 text-sm text-text-neutral-medium">
                Showing
                <span class="font-semibold text-text-neutral-high">{{ visibleEntries.length }}</span>
                of
                <span class="font-semibold text-text-neutral-high">{{ filteredEntries.length }}</span>
              </div>
            </div>
          </section>

          <section>
            <TransitionGroup
                name="log-event"
                tag="div"
                class="flex flex-col gap-3"
            >
              <article
                  v-for="entry in visibleEntries"
                  :key="entry.id"
                  class="overflow-hidden rounded-[18px] border bg-layer-2 dropshadow-heavy"
                  :class="getEntryBorderClass(entry)"
              >
                <div class="flex flex-col gap-3 p-4 md:flex-row md:items-start md:justify-between">
                  <div class="min-w-0">
                    <div class="flex flex-wrap items-center gap-2">
                      <span
                          class="inline-flex items-center rounded-full px-3 py-1 text-xs font-semibold"
                          :class="getTypeBadgeClass(entry.type)"
                      >
                        {{ entry.type }}
                      </span>

                      <span class="break-words text-lg font-semibold text-text-neutral-high">
                        {{ entry.event }}
                      </span>

                      <span
                          v-if="isConnectionChanged(entry) && entry.payload?.offline"
                          class="inline-flex items-center gap-1 rounded-full bg-red-500/20 px-3 py-1 text-xs font-semibold text-red-300"
                      >
                        <NoSymbolIcon class="h-4 w-4" />
                        Offline
                      </span>
                    </div>

                    <div class="mt-2 flex items-center gap-2 text-xs text-text-neutral-low">
                      <ClockIcon class="h-4 w-4" />
                      <time>{{ formatDate(entry.timestamp) }}</time>
                    </div>
                  </div>

                  <button
                      type="button"
                      class="inline-flex cursor-pointer items-center gap-2 rounded-[12px] bg-button-secondary-mono-resting px-3 py-2 text-sm font-semibold text-text-neutral-high transition hover:opacity-85"
                      @click="copyEntry(entry)"
                  >
                    <ClipboardDocumentIcon class="h-5 w-5" />
                    Copy
                  </button>
                </div>

                <div
                    v-if="isConnectionChanged(entry)"
                    class="mx-4 mb-4 grid gap-3 rounded-[16px] bg-primary/10 p-4 text-sm text-text-neutral-high md:grid-cols-2 xl:grid-cols-4"
                >
                  <div>
                    <div class="text-xs text-text-neutral-low">
                      Label
                    </div>

                    <div class="mt-1 font-semibold">
                      {{ entry.payload?.label?.from || '—' }}
                      →
                      {{ entry.payload?.label?.to || '—' }}
                    </div>
                  </div>

                  <div>
                    <div class="text-xs text-text-neutral-low">
                      Direction
                    </div>

                    <div class="mt-1 font-semibold capitalize">
                      {{ entry.payload?.direction || '—' }}
                    </div>
                  </div>

                  <div>
                    <div class="text-xs text-text-neutral-low">
                      Quality
                    </div>

                    <div class="mt-1 font-semibold">
                      {{ entry.payload?.quality?.from || '—' }}
                      →
                      {{ entry.payload?.quality?.to || '—' }}
                    </div>
                  </div>

                  <div>
                    <div class="text-xs text-text-neutral-low">
                      Details
                    </div>

                    <div class="mt-1 font-semibold">
                      {{ entry.payload?.quality?.bars ?? '—' }} bars
                      <span v-if="entry.payload?.quality?.latency">
                        · {{ entry.payload.quality.latency }}ms
                      </span>
                    </div>
                  </div>
                </div>

                <Disclosure v-slot="{ open }">
                  <DisclosureButton class="flex w-full cursor-pointer items-center justify-between border-t border-white/10 px-4 py-3 text-left text-sm font-semibold text-primary transition hover:bg-white/5">
                    <span>Payload</span>
                    <ChevronDownIcon class="h-5 w-5 transition" :class="{ 'rotate-180': open }" />
                  </DisclosureButton>

                  <DisclosurePanel class="border-t border-white/10 bg-black/20 p-4">
                    <pre class="max-h-[420px] overflow-auto rounded-[14px] bg-black/30 p-4 text-xs leading-5 text-text-neutral-medium">{{ stringify(entry.payload) }}</pre>
                  </DisclosurePanel>
                </Disclosure>

                <Disclosure v-if="entry.meta" v-slot="{ open }">
                  <DisclosureButton class="flex w-full cursor-pointer items-center justify-between border-t border-white/10 px-4 py-3 text-left text-sm font-semibold text-primary transition hover:bg-white/5">
                    <span>Meta</span>
                    <ChevronDownIcon class="h-5 w-5 transition" :class="{ 'rotate-180': open }" />
                  </DisclosureButton>

                  <DisclosurePanel class="border-t border-white/10 bg-black/20 p-4">
                    <pre class="max-h-[420px] overflow-auto rounded-[14px] bg-black/30 p-4 text-xs leading-5 text-text-neutral-medium">{{ stringify(entry.meta) }}</pre>
                  </DisclosurePanel>
                </Disclosure>
              </article>
            </TransitionGroup>

            <button
                v-if="hiddenEntriesCount > 0"
                type="button"
                class="mt-3 w-full cursor-pointer rounded-[14px] bg-button-secondary-mono-resting px-4 py-3 text-sm font-semibold text-text-neutral-high transition hover:opacity-85"
                @click="showMoreEntries"
            >
              Show more ({{ hiddenEntriesCount }} hidden)
            </button>

            <div
                v-if="filteredEntries.length === 0"
                class="mt-3 rounded-[24px] bg-layer-2 p-8 text-center text-text-neutral-medium dropshadow-heavy"
            >
              No events match the current filters.
            </div>
          </section>
        </main>
      </div>
    </div>
  </div>
</template>

<script>
import {
  ArrowDownTrayIcon,
  ArrowPathIcon,
  ArrowUpTrayIcon,
  ArrowsUpDownIcon,
  CheckIcon,
  ChevronDownIcon,
  ChevronRightIcon,
  ChevronUpDownIcon,
  ClipboardDocumentIcon,
  ClockIcon,
  DocumentTextIcon,
  InboxIcon,
  MagnifyingGlassIcon,
  NoSymbolIcon,
  ServerStackIcon,
  TrashIcon,
} from '@heroicons/vue/24/outline';

import {
  Disclosure,
  DisclosureButton,
  DisclosurePanel,
  Listbox,
  ListboxButton,
  ListboxOption,
  ListboxOptions,
} from '@headlessui/vue';

import { markRaw } from 'vue';

import {
  clearSessionEntries,
  deleteAllSessions,
  importSessions,
  readSession,
  readSessionSummaries,
} from '@/services/session-logs-db.service';

const ENTRIES_PAGE_SIZE = 200;

export default {
  name: 'SessionLogs',

  components: {
    ArrowDownTrayIcon,
    ArrowPathIcon,
    ArrowsUpDownIcon,
    CheckIcon,
    ChevronDownIcon,
    ChevronRightIcon,
    ChevronUpDownIcon,
    ClipboardDocumentIcon,
    ClockIcon,
    Disclosure,
    DisclosureButton,
    DisclosurePanel,
    DocumentTextIcon,
    InboxIcon,
    Listbox,
    ListboxButton,
    ListboxOption,
    ListboxOptions,
    MagnifyingGlassIcon,
    NoSymbolIcon,
    ServerStackIcon,
    TrashIcon,
    ArrowUpTrayIcon,
  },

  data() {
    return {
      sessions: [],
      selectedEntries: [],
      loadedEntriesKey: null,
      entriesLoadInFlight: false,
      visibleEntriesLimit: ENTRIES_PAGE_SIZE,
      selectedRoomCode: null,
      search: '',
      selectedType: '',
      selectedSender: '',
      sortDirection: 'desc',
      refreshInterval: null,
      refreshRequestId: 0,
      appliedRefreshRequestId: 0,
    };
  },

  computed: {
    selectedSession() {
      return this.sessions.find(session => session.roomCode === this.selectedRoomCode) || null;
    },

    availableTypes() {
      return [...new Set(
          this.selectedEntries
              .map(entry => entry.type)
              .filter(Boolean)
      )].sort();
    },

    availableSenders() {
      return [...new Set(
          this.selectedEntries
              .map(entry => entry.payload?.sender?.name)
              .filter(Boolean)
      )].sort((a, b) => String(a).localeCompare(String(b)));
    },

    // База отдаёт записи по возрастанию времени.
    orderedEntries() {
      return this.sortDirection === 'desc'
          ? [...this.selectedEntries].reverse()
          : this.selectedEntries;
    },

    filteredEntries() {
      const search = this.search.trim().toLowerCase();

      return this.orderedEntries.filter(entry => {
        const matchesType = this.selectedType
            ? entry.type === this.selectedType
            : true;

        if (!matchesType) return false;

        if (this.selectedSender && entry.payload?.sender?.name !== this.selectedSender) return false;

        if (!search) return true;

        const haystack = [
          entry.type,
          entry.event,
          entry.timestamp,
          JSON.stringify(entry.payload),
          JSON.stringify(entry.meta),
        ]
            .filter(Boolean)
            .join(' ')
            .toLowerCase();

        return haystack.includes(search);
      });
    },

    visibleEntries() {
      return this.filteredEntries.slice(0, this.visibleEntriesLimit);
    },

    hiddenEntriesCount() {
      return this.filteredEntries.length - this.visibleEntries.length;
    },

    sortDirectionLabel() {
      return this.sortDirection === 'desc' ? 'Newest first' : 'Oldest first';
    },
  },

  watch: {
    search() {
      this.visibleEntriesLimit = ENTRIES_PAGE_SIZE;
    },

    selectedType() {
      this.visibleEntriesLimit = ENTRIES_PAGE_SIZE;
    },

    selectedSender() {
      this.visibleEntriesLimit = ENTRIES_PAGE_SIZE;
    },
  },

  mounted() {
    this.refreshLogs();

    this.refreshInterval = window.setInterval(() => {
      this.refreshLogs();
    }, 1000);
  },

  beforeUnmount() {
    if (this.refreshInterval) {
      window.clearInterval(this.refreshInterval);
      this.refreshInterval = null;
    }
  },

  methods: {
    toggleSortDirection() {
      this.sortDirection = this.sortDirection === 'desc' ? 'asc' : 'desc';
      this.visibleEntriesLimit = ENTRIES_PAGE_SIZE;
    },

    showMoreEntries() {
      this.visibleEntriesLimit += ENTRIES_PAGE_SIZE;
    },

    async refreshLogs() {
      // Чтения могут завершиться не по порядку: результат более старого запроса
      // не должен вернуть на экран, например, уже очищенные логи.
      const requestId = ++this.refreshRequestId;
      let summaries;

      try {
        summaries = await readSessionSummaries();
      } catch (error) {
        console.warn('[SessionLogs] Failed to read logs', error);
        return;
      }

      if (requestId < this.appliedRefreshRequestId) return;

      this.appliedRefreshRequestId = requestId;

      const previousSelectedRoomCode = this.selectedRoomCode;

      this.sessions = summaries.sort((a, b) => {
        return new Date(b.updatedAt).getTime() - new Date(a.updatedAt).getTime();
      });

      if (!this.sessions.length) {
        this.selectedRoomCode = null;
        this.selectedEntries = [];
        this.loadedEntriesKey = null;
        return;
      }

      const selectedStillExists = this.sessions.some(
          session => session.roomCode === previousSelectedRoomCode
      );

      this.selectedRoomCode = selectedStillExists
          ? previousSelectedRoomCode
          : this.sessions[0].roomCode;

      await this.loadSelectedEntries();
    },

    // Записи читаем только когда сессия изменилась: чтение полной сессии занимает сотни миллисекунд.
    // Одновременно идёт одна загрузка; если за это время сессия изменилась, ключ не совпадёт
    // и следующий опрос прочитает её заново.
    async loadSelectedEntries() {
      const { roomCode, updatedAt, entryCount } = this.selectedSession;
      const key = `${roomCode}|${updatedAt}|${entryCount}`;

      if (key === this.loadedEntriesKey || this.entriesLoadInFlight) return;

      this.entriesLoadInFlight = true;

      try {
        const session = await readSession(roomCode);

        if (roomCode === this.selectedRoomCode) {
          // Десятки тысяч записей только читаются, глубокая реактивность им не нужна.
          this.selectedEntries = markRaw(session?.entries || []);
          this.loadedEntriesKey = key;
        }
      } catch (error) {
        console.warn('[SessionLogs] Failed to read session entries', error);
      } finally {
        this.entriesLoadInFlight = false;
      }
    },

    selectSession(roomCode) {
      this.selectedRoomCode = roomCode;
      this.selectedEntries = [];
      this.loadedEntriesKey = null;
      this.visibleEntriesLimit = ENTRIES_PAGE_SIZE;
      this.search = '';
      this.selectedType = '';
      this.selectedSender = '';

      return this.refreshLogs();
    },

    async exportSelectedSession() {
      if (!this.selectedSession) return;

      const session = await readSession(this.selectedSession.roomCode);

      if (!session) return;

      const blob = new Blob([JSON.stringify(session, null, 2)], {
        type: 'application/json',
      });

      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');

      link.href = url;
      link.download = `session-${session.roomCode}-logs.json`;

      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);

      URL.revokeObjectURL(url);
    },

    async clearSelectedSession() {
      if (!this.selectedSession) return;

      const roomCode = this.selectedSession.roomCode;

      if (!window.confirm(`Clear events for session ${roomCode}?`)) {
        return;
      }

      try {
        await clearSessionEntries(roomCode);
      } catch (error) {
        console.warn('[SessionLogs] Failed to clear session', error);
      }

      await this.refreshLogs();
    },

    async clearAllLogs() {
      if (!window.confirm('Clear all session logs?')) {
        return;
      }

      try {
        await deleteAllSessions();
      } catch (error) {
        console.warn('[SessionLogs] Failed to clear logs', error);
      }

      await this.refreshLogs();
    },

    async copyEntry(entry) {
      const text = JSON.stringify(entry, null, 2);

      try {
        await navigator.clipboard.writeText(text);
      } catch (error) {
        console.warn('[SessionLogs] Failed to copy entry', error);
      }
    },

    formatDate(value) {
      if (!value) return '—';

      const date = new Date(value);

      if (Number.isNaN(date.getTime())) {
        return value;
      }

      return date.toLocaleString();
    },

    stringify(value) {
      if (value === undefined) return '';

      try {
        return JSON.stringify(value, null, 2);
      } catch {
        return String(value);
      }
    },

    isConnectionChanged(entry) {
      return entry.type === 'custom' && entry.event === 'connection_changed';
    },

    getEntryBorderClass(entry) {
      if (String(entry.type).includes('error')) {
        return 'border-l-4 border-l-red-400 border-white/10';
      }

      if (entry.type === 'socket:incoming') {
        return 'border-l-4 border-l-primary border-white/10';
      }

      if (entry.type === 'socket:outgoing') {
        return 'border-l-4 border-l-green-400 border-white/10';
      }

      if (entry.type === 'socket:connection') {
        return 'border-l-4 border-l-purple-400 border-white/10';
      }

      if (entry.type === 'custom') {
        return 'border-l-4 border-l-yellow-400 border-white/10';
      }

      if (entry.type === 'asr') {
        return 'border-l-4 border-l-cyan-400 border-white/10';
      }

      return 'border-l-4 border-l-white/20 border-white/10';
    },

    getTypeBadgeClass(type) {
      if (String(type).includes('error')) {
        return 'bg-red-400/15 text-red-300';
      }

      if (type === 'socket:incoming') {
        return 'bg-primary/15 text-primary';
      }

      if (type === 'socket:outgoing') {
        return 'bg-green-400/15 text-green-300';
      }

      if (type === 'socket:connection') {
        return 'bg-purple-400/15 text-purple-300';
      }

      if (type === 'custom') {
        return 'bg-yellow-400/15 text-yellow-300';
      }

      if (type === 'asr') {
        return 'bg-cyan-400/15 text-cyan-300';
      }

      return 'bg-white/10 text-text-neutral-medium';
    },
    async importLogsFromFile(event) {
      const file = event.target.files?.[0];

      if (!file) return;

      try {
        const text = await file.text();
        const parsed = JSON.parse(text);

        const importedSessions = this.normalizeImportedLogs(parsed);

        if (!importedSessions.length) {
          window.alert('No valid session logs found in this file.');
          return;
        }

        await importSessions(importedSessions);
        await this.selectSession(importedSessions[0].roomCode);

        window.alert(`Imported ${importedSessions.length} session(s).`);
      } catch (error) {
        console.warn('[SessionLogs] Failed to import logs', error);
        window.alert('Failed to import JSON file. Please check the file format.');
      } finally {
        if (this.$refs.importInput) {
          this.$refs.importInput.value = '';
        }
      }
    },

    normalizeImportedLogs(parsed) {
      const rawSessions = Array.isArray(parsed) ? parsed : [parsed];

      return rawSessions
          .filter(session => {
            return session &&
                typeof session === 'object' &&
                session.roomCode &&
                Array.isArray(session.entries);
          })
          .map(session => {
            const now = new Date().toISOString();

            return {
              roomCode: String(session.roomCode),
              userId: session.userId || null,
              startedAt: session.startedAt || now,
              updatedAt: session.updatedAt || now,
              importedAt: now,
              entries: session.entries
                  .filter(entry => entry && typeof entry === 'object')
                  .map(entry => ({
                    id: entry.id || `${Date.now()}-${Math.random().toString(16).slice(2)}`,
                    timestamp: entry.timestamp || now,
                    type: entry.type || 'unknown',
                    event: entry.event || 'unknown',
                    payload: entry.payload ?? null,
                    meta: entry.meta ?? null,
                  })),
            };
          });
    },
  },
};
</script>

<style scoped>
.log-event-enter-active,
.log-event-leave-active {
  transition: opacity 180ms ease, transform 180ms ease;
}

.log-event-enter-from {
  opacity: 0;
  transform: translateY(-8px);
}

.log-event-leave-to {
  opacity: 0;
  transform: translateY(8px);
}

.log-event-move {
  transition: transform 180ms ease;
}
</style>